<!--
  The journal's free-text search, in the page header. It writes the `search` filter the same way
  the filter bar did; ⌘K (Ctrl+K off macOS) focuses it from anywhere on the page.
-->
<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type {
  TransactionFilterPatch,
  TransactionFilterQuery,
} from '../composables/use-transaction-filters.ts'

const props = defineProps<{ search: TransactionFilterQuery['search'] }>()

const emit = defineEmits<{ filter: [patch: TransactionFilterPatch] }>()

const { t } = useI18n()
const input = ref<HTMLInputElement>()
const SEARCH_DEBOUNCE_MS = 400
let searchDebounceTimer: ReturnType<typeof setTimeout> | undefined
let lastEmittedSearch: string | undefined

function cancelPendingSearch(): void {
  clearTimeout(searchDebounceTimer)
}

function onSearchInput(event: Event): void {
  const value = (event.target as HTMLInputElement).value
  cancelPendingSearch()
  searchDebounceTimer = setTimeout(() => {
    lastEmittedSearch = value === '' ? undefined : value
    emit('filter', { search: lastEmittedSearch })
  }, SEARCH_DEBOUNCE_MS)
}

function reset(): void {
  cancelPendingSearch()
  if (input.value !== undefined) input.value.value = ''
}

watch(
  () => props.search,
  (search) => {
    if (search !== lastEmittedSearch) cancelPendingSearch()
  },
)
defineExpose({ reset })

function onShortcut(event: KeyboardEvent): void {
  if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return
  event.preventDefault()
  input.value?.focus()
}

onMounted(() => window.addEventListener('keydown', onShortcut))
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onShortcut)
  cancelPendingSearch()
})
</script>

<template>
  <label
    class="flex h-[var(--density-control)] w-72 items-center gap-[var(--space-inline)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text-muted)] focus-within:border-[color:var(--color-focus)] focus-within:outline focus-within:outline-[length:var(--ring-focus-width)] focus-within:outline-offset-[length:var(--ring-focus-offset)] focus-within:outline-[color:var(--color-focus)]"
  >
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      stroke-width="1.7"
      class="shrink-0"
    >
      <circle cx="7" cy="7" r="4.4" />
      <path d="M10.2 10.2 13.6 13.6" />
    </svg>
    <input
      ref="input"
      type="search"
      :value="search ?? ''"
      :aria-label="t('transactions.filters.search')"
      :placeholder="t('transactions.filters.searchPlaceholder')"
      class="h-full min-w-0 flex-1 bg-transparent text-[length:var(--text-step-2)] text-[color:var(--color-text)] outline-none placeholder:text-[color:var(--color-text-muted)]"
      @input="onSearchInput"
    />
    <kbd
      aria-hidden="true"
      class="shrink-0 rounded-[var(--radius-control)] border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-[var(--space-tight)] font-sans text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
    >
      ⌘ K
    </kbd>
  </label>
</template>
